import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Mail, Menu, X } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/wocjce1q_Logo-Lackcenter-NEU.png";
const PHONE_NUMBER = "tel:+493916357115";
const EMAIL = "mailto:info@lackzentrum-wilke.com";

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#leistungen", label: "Leistungen" },
  { href: "#galerie", label: "Galerie" },
  { href: "#ueber-uns", label: "Über uns" },
  { href: "#kontakt", label: "Kontakt" },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-lg py-2"
          : "bg-accent/95 backdrop-blur-md py-3"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#home" 
            onClick={(e) => scrollToSection(e, "#home")}
            className="flex items-center gap-3"
          >
            <img
              src={COMPANY_LOGO}
              alt="Lack Zentrum Wilke Logo"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isScrolled 
                    ? "text-foreground hover:text-primary" 
                    : "text-accent-foreground hover:text-primary-light"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant={isScrolled ? "outline" : "ghost"}
              size="sm"
              asChild
              className={`gap-2 ${!isScrolled ? "text-accent-foreground border-accent-foreground/30 hover:bg-accent-foreground/10" : ""}`}
            >
              <a href={EMAIL}>
                <Mail className="h-4 w-4" />
                E-Mail
              </a>
            </Button>
            <Button
              variant="primary"
              size="sm"
              asChild
              className="gap-2"
            >
              <a href={PHONE_NUMBER}>
                <Phone className="h-4 w-4" />
                Anrufen
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              isScrolled ? "hover:bg-muted" : "hover:bg-accent-foreground/10"
            }`}
            aria-label="Menü öffnen"
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${isScrolled ? "text-foreground" : "text-accent-foreground"}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isScrolled ? "text-foreground" : "text-accent-foreground"}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg animate-fade-in">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="py-3 px-4 text-foreground font-medium hover:bg-muted rounded-md transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border">
                <Button variant="outline" asChild className="w-full gap-2">
                  <a href={EMAIL}>
                    <Mail className="h-4 w-4" />
                    E-Mail senden
                  </a>
                </Button>
                <Button variant="primary" asChild className="w-full gap-2">
                  <a href={PHONE_NUMBER}>
                    <Phone className="h-4 w-4" />
                    Jetzt anrufen
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
